import { ColumnSelectionItem } from './ColumnSelection.props';
import { CheckItemProps } from '../../organisms/CheckItemGroup';

/** Sentinel for "no group filter" — the sidebar's first entry. */
export const ALL_GROUPS = '__all__';

/** A group narrowed to the rows the user can currently see. */
export interface GroupView {
  /**
   * Position in the caller's array. Every mutation targets this rather than the
   * title: `ColumnSelectionItem` carries no id, and two groups sharing a title
   * would otherwise be written to together.
   */
  index: number;
  title: string;
  /** Items passing the search / selected-only filters, in their original order. */
  items: Array<CheckItemProps>;
  /** Ticked items among `items`. */
  selected: number;
  /** Items in the group hidden by the current filters. */
  hidden: number;
}

/** Per-group tallies for the sidebar. */
export interface GroupSummary {
  title: string;
  selected: number;
  total: number;
}

/**
 * Split a query into lowercase tokens so word order doesn't matter — "id job"
 * and "job id" both find "Job ID". A plain substring match would find neither.
 */
export const tokenise = (query: string): Array<string> =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean);

/**
 * Every token must appear somewhere in the title or the item's `keywords`.
 *
 * `keywords` is what makes the synthetic master toggles findable: ticking
 * "Service Address (Split)" reveals seven columns whose own titles (Suburb,
 * Postcode, …) never appear in the picker, so without them a search for
 * "postcode" on Jobs would wrongly report nothing.
 */
export const matchesQuery = (item: CheckItemProps, tokens: Array<string>): boolean => {
  if (!tokens.length) return true;
  const haystack = `${item.title} ${item.keywords?.join(' ') ?? ''}`.toLowerCase();
  return tokens.every((token) => haystack.includes(token));
};

/**
 * Sidebar tallies. Counts are match-scoped while a search is running, so
 * "Structure 1/4" means one of the four matches is ticked. Groups with no
 * matches are dropped by the caller, not here.
 */
export const summariseGroups = (
  groups: Array<ColumnSelectionItem>,
  tokens: Array<string>
): Array<GroupSummary> =>
  groups.map((group) => {
    const matching = group.value.filter((item) => matchesQuery(item, tokens));
    return {
      title: group.title,
      selected: matching.filter((item) => item.value).length,
      total: matching.length,
    };
  });

/**
 * Build the right pane. Order is preserved throughout: `utils/services.ts`
 * reaches into `columnSelectionItems[2]` and `[7]` by index to resolve the
 * synthetic address/interaction toggles, so sorting or renesting groups
 * anywhere in this file would silently break the Jobs grid.
 */
export const buildGroupViews = (
  groups: Array<ColumnSelectionItem>,
  tokens: Array<string>,
  activeGroup: string,
  selectedOnly: boolean,
  sticky: ReadonlySet<string> = new Set()
): Array<GroupView> =>
  groups
    .map((group, index) => {
      const items = group.value.filter(
        (item) =>
          matchesQuery(item, tokens) &&
          (!selectedOnly || item.value || sticky.has(stickyKey(index, item.field)))
      );
      return {
        index,
        title: group.title,
        items,
        selected: items.filter((item) => item.value).length,
        hidden: group.value.length - items.length,
      };
    })
    .filter(
      (view) => view.items.length > 0 && (activeGroup === ALL_GROUPS || view.title === activeGroup)
    );

/** Group-scoped key — `field` only has to be unique within its own group. */
export const stickyKey = (groupIndex: number, field: string): string => `${groupIndex}::${field}`;

/** Drives the tri-state master checkbox on a group header. */
export const groupCheckState = (view: GroupView): 'none' | 'some' | 'all' => {
  if (!view.items.length || view.selected === 0) return 'none';
  return view.selected === view.items.length ? 'all' : 'some';
};

/**
 * Set `value` on exactly the listed fields of one group, leaving every other
 * field — including the ones the current search has hidden — untouched. That
 * restraint is the whole point: a "Deselect all" run while a search is active
 * must not silently clear ticks the user cannot see.
 */
export const setFieldsInGroup = (
  groups: Array<ColumnSelectionItem>,
  groupIndex: number,
  fields: ReadonlySet<string>,
  value: boolean
): Array<ColumnSelectionItem> =>
  groups.map((group, index) =>
    index === groupIndex
      ? {
          ...group,
          value: group.value.map((item) => (fields.has(item.field) ? { ...item, value } : item)),
        }
      : group
  );

/** Flip one field in one group. */
export const applyToggle = (
  groups: Array<ColumnSelectionItem>,
  groupIndex: number,
  field: string,
  value: boolean
): Array<ColumnSelectionItem> => setFieldsInGroup(groups, groupIndex, new Set([field]), value);

/** Apply a bulk action across several groups at once, honouring each group's visible fields. */
export const setFieldsAcrossGroups = (
  groups: Array<ColumnSelectionItem>,
  views: Array<GroupView>,
  value: boolean
): Array<ColumnSelectionItem> =>
  views.reduce(
    (acc, view) =>
      setFieldsInGroup(acc, view.index, new Set(view.items.map((item) => item.field)), value),
    groups
  );

/** Total ticked / total available, across every group and ignoring filters. */
export const countSelected = (
  groups: Array<ColumnSelectionItem>
): { selected: number; total: number } =>
  groups.reduce(
    (acc, group) => ({
      selected: acc.selected + group.value.filter((item) => item.value).length,
      total: acc.total + group.value.length,
    }),
    { selected: 0, total: 0 }
  );
